import { computed, ref, watch } from 'vue';
import { useModernGridHelper } from '@/common/components/molecules/modernGrid/modernGrid.uses';
import type { Props, Emit } from '@/common/components/molecules/grid/baseGrid.setup';
import type {
  CustomColumn,
  RowData,
  ClickedRowInfo,
  DblClickedRowInfo,
  ClickedCellInfo,
  ClickedCellInfoForTransformed,
  ColumnWithHideInfo,
} from '@/common/utils/types';
import type { VisibilityState, SortingState } from '@tanstack/vue-table';
import type { FilterSelectedValues } from '@/common/components/molecules/filterSearch/filterSearch.type';
import type {
  GridColumnDef,
  GridRawRowDataType,
  GridOptionV2,
  PaginationEmitValue,
  GridDataFilter,
} from '@/common/components/molecules/modernGrid/modernGrid.types';

import {
  transformGridOption,
  transformSelectedRows,
  reverseTransformSelectedRow,
  transformPaginationEvent,
  extractTextFilterFromChangeFilter,
  transformSortingEvent,
  transformClickRowEvent,
  createBaseGridSlotItem,
  isArrayRows,
  transformArrayRowsToObjects,
  transformObjectRowToArray,
  createFieldToIndexMap,
  buildNodeIndices,
  applyCustomAscFunction,
  transformHighlightedRows,
  transformSelectedRowsToHighlightedRows,
  transformEmptyText,
  findRowIndex,
  determineCheckEvent,
  isSameRow,
  type BaseGridCompatibleSlotItem,
  type BaseGridAdapterOptions,
} from './utils';
import { COLUMN_EVENT_TYPE, DETAIL_ROW_KEY } from '../grid/baseGrid.define';

// --- Proxy Helper ---

const createArrayRowProxy = <RowDataType>(
  rowArray: unknown[],
  fieldToIndex: Record<string, number>,
  needsDetailRow: boolean,
  columns: CustomColumn[],
): RowDataType => {
  return new Proxy({} as Record<string, unknown>, {
    get(_target, prop) {
      if (typeof prop === 'string' && prop in fieldToIndex) {
        return rowArray[fieldToIndex[prop]];
      }
      if (prop === DETAIL_ROW_KEY && needsDetailRow) {
        const obj: Record<string, unknown> = { isDetail: true };
        columns.forEach((col, idx) => {
          obj[col.field] = rowArray[idx];
        });
        return [obj];
      }
      if (typeof prop === 'symbol') return undefined;
      const numProp = Number(prop);
      if (!Number.isNaN(numProp) && numProp >= 0 && numProp < rowArray.length) {
        return rowArray[numProp];
      }
      return undefined;
    },
    set(_target, prop, value) {
      if (typeof prop === 'string' && prop in fieldToIndex) {
        rowArray[fieldToIndex[prop]] = value;
        return true;
      }
      const numProp = Number(prop);
      if (!Number.isNaN(numProp) && numProp >= 0) {
        rowArray[numProp] = value;
        return true;
      }
      return true;
    },
    has(_target, prop) {
      if (typeof prop === 'string' && prop in fieldToIndex) return true;
      if (prop === DETAIL_ROW_KEY && needsDetailRow) return true;
      return false;
    },
    ownKeys() {
      const keys = columns.map((col) => col.field);
      if (needsDetailRow) keys.push(DETAIL_ROW_KEY);
      return [...new Set(keys)];
    },
    getOwnPropertyDescriptor(_target, prop) {
      if (typeof prop === 'string' && (prop in fieldToIndex || prop === DETAIL_ROW_KEY)) {
        return { enumerable: true, configurable: true, writable: true };
      }
      return undefined;
    },
  }) as RowDataType;
};

// --- Main Hook ---

export const useBaseGridAdapter = <
  ColumnData extends CustomColumn[] | Readonly<CustomColumn[]>,
  RowDataType extends GridRawRowDataType = RowData<ColumnData>,
>(
  props: Props<ColumnData, RowDataType>,
  emit: Emit<ColumnData, RowDataType>,
  adapterOptions?: BaseGridAdapterOptions,
) => {
  const { transformColumns } = useModernGridHelper<RowDataType>();
  const columnsArray = computed<CustomColumn[]>(() => props.columns as CustomColumn[]);

  // --- Row Transform Helpers ---

  const transformRowsIfArray = <T extends Record<string, unknown>>(
    rows: T[] | unknown[][],
  ): T[] => {
    if (!rows || rows.length === 0) return [];
    if (Array.isArray(rows[0])) {
      return transformArrayRowsToObjects<T>(rows as unknown[][], columnsArray.value);
    }
    return rows as T[];
  };

  const findMatchingRowsInModernRows = (
    modernRowsList: RowDataType[],
    targetRows: RowDataType[],
  ): RowDataType[] => {
    if (!targetRows.length || !modernRowsList.length) return [];

    const targetRowIds = targetRows
      .map((r) => (r as Record<string, unknown>).rowId as string | undefined)
      .filter((id): id is string => id !== undefined);

    if (targetRowIds.length === targetRows.length) {
      const targetIdSet = new Set(targetRowIds);
      return modernRowsList.filter((row) => {
        const rowId = (row as Record<string, unknown>).rowId as string | undefined;
        return rowId !== undefined && targetIdSet.has(rowId);
      });
    }

    return modernRowsList.filter((row) => targetRows.some((target) => isSameRow(row, target)));
  };

  const transformSingleRowIfArray = <T>(row: T | unknown[] | undefined): T | undefined => {
    if (!row) return undefined;
    if (Array.isArray(row)) {
      const obj: Record<string, unknown> = {};
      columnsArray.value.forEach((col, idx) => {
        obj[col.field] = (row as unknown[])[idx];
      });
      return obj as T;
    }
    return row as T;
  };

  // --- Props Transform ---

  const modernColumns = computed<GridColumnDef<RowDataType>[]>(() => {
    const columns = transformColumns(columnsArray.value);
    return applyCustomAscFunction<RowDataType>(columns, props.option?.customAscFunction);
  });

  const modernOption = computed<GridOptionV2>(() => {
    return transformGridOption(
      props.option,
      props.searchBarOption,
      props.pagination,
      props.exportExcelOption,
      adapterOptions,
    );
  });

  const modernSelectedRow = computed<RowDataType | undefined>(() => {
    const selected = transformSelectedRows(props.selectedRows);
    return transformSingleRowIfArray<RowDataType>(selected);
  });

  const modernHighlightedRows = computed<RowDataType[]>(() => {
    const highlightFromOption = transformHighlightedRows(
      modernRows.value,
      props.option?.style?.highlight,
    );
    const highlightFromSelectedRows = transformSelectedRowsToHighlightedRows(
      transformRowsIfArray<RowDataType>(props.selectedRows ?? []),
    );
    return [...highlightFromOption, ...highlightFromSelectedRows];
  });

  const modernExpandedRows = computed<RowDataType[]>(() => {
    const expanded = props.expanded ?? [];
    if (expanded.length === 0) return [];
    if (Array.isArray(expanded[0])) {
      return transformArrayRowsToObjects<RowDataType>(
        expanded as unknown as unknown[][],
        columnsArray.value,
      );
    }
    return expanded as RowDataType[];
  });

  const modernEnvKey = computed<string | undefined>(() => props.columnEnvKey);

  // --- Internal State ---

  const internalSelectedRow = ref<RowDataType | undefined>(modernSelectedRow.value);
  const internalCheckedRows = ref<RowDataType[]>(
    transformRowsIfArray<RowDataType>(props.checkedRows ?? []),
  );
  const internalExpandedRows = ref<RowDataType[]>(modernExpandedRows.value);

  watch(
    () => props.selectedRows,
    (newVal) => {
      const selected = transformSelectedRows(newVal);
      internalSelectedRow.value = transformSingleRowIfArray<RowDataType>(selected);
    },
  );

  watch(
    () => props.checkedRows,
    (newVal) => {
      internalCheckedRows.value = transformRowsIfArray<RowDataType>(newVal ?? []);
    },
  );

  watch(
    () => props.expanded,
    (newVal) => {
      if (newVal && newVal.length > 0 && Array.isArray(newVal[0])) {
        internalExpandedRows.value = transformArrayRowsToObjects<RowDataType>(
          newVal as unknown as unknown[][],
          columnsArray.value,
        );
      } else {
        internalExpandedRows.value = (newVal ?? []) as RowDataType[];
      }
    },
  );

  // --- Row Transform ---

  const isRowsArray = computed(() => isArrayRows(props.rows as unknown[]));
  const useDetailRow = computed(() => props.option?.rowDetail?.use ?? false);

  const modernRows = computed<RowDataType[]>(() => {
    const currentRows = props.rows as unknown[];
    if (!currentRows || currentRows.length === 0) return [];

    const isArrayFormat = isRowsArray.value;
    const needsDetailRow = useDetailRow.value;

    if (!isArrayFormat && !needsDetailRow) return currentRows as RowDataType[];

    if (!isArrayFormat && needsDetailRow) {
      return (currentRows as RowDataType[]).map((row) => {
        if ((row as Record<string, unknown>)[DETAIL_ROW_KEY]) return row;
        return {
          ...row,
          [DETAIL_ROW_KEY]: [{ isDetail: true, ...row }],
        } as RowDataType;
      });
    }

    const fieldToIndex: Record<string, number> = {};
    columnsArray.value.forEach((col, idx) => {
      fieldToIndex[col.field] = idx;
    });

    return (currentRows as unknown[][]).map((rowArray) =>
      createArrayRowProxy<RowDataType>(rowArray, fieldToIndex, needsDetailRow, columnsArray.value),
    );
  });

  // --- Format Conversion ---

  const convertRowsToOriginalFormat = (rows: RowDataType[]): RowDataType[] | unknown[][] => {
    if (isRowsArray.value) {
      return rows.map((row) =>
        transformObjectRowToArray(row as Record<string, unknown>, columnsArray.value),
      );
    }
    return rows;
  };

  const convertRowToOriginalFormat = <T extends Record<string, unknown>>(
    rowData: T,
  ): T | unknown[] => {
    if (isRowsArray.value) {
      return transformObjectRowToArray(rowData, columnsArray.value);
    }
    return rowData;
  };

  // --- Event Handlers ---

  const handleUpdateSelectedRow = (row: RowDataType | undefined) => {
    internalSelectedRow.value = row;
    if (row && isRowsArray.value) {
      const rowAsArray = transformObjectRowToArray(
        row as Record<string, unknown>,
        columnsArray.value,
      );
      emit('update:selected-rows', [rowAsArray] as unknown as RowDataType[]);
    } else {
      emit('update:selected-rows', reverseTransformSelectedRow(row));
    }
  };

  const handleUpdateCheckedRows = (rows: RowDataType[]) => {
    const prevRows = internalCheckedRows.value;
    internalCheckedRows.value = rows;

    const finalRows = convertRowsToOriginalFormat(rows);
    emit('update:checked-rows', finalRows as RowDataType[]);

    const checkEvent = determineCheckEvent(
      prevRows as RowDataType[],
      rows,
      modernRows.value as RowDataType[],
    );

    if (checkEvent?.type === 'checkAll') {
      const eventRows = convertRowsToOriginalFormat(checkEvent.rows as RowDataType[]);
      emit('check-all', checkEvent.isCheck, eventRows as RowDataType[]);
    } else if (checkEvent?.type === 'checkRow') {
      const finalRow = convertRowToOriginalFormat(checkEvent.row as Record<string, unknown>);
      emit('check-row', checkEvent.isCheck, checkEvent.rowIndex, finalRow as RowDataType);
    }
  };

  const handleUpdateExpandedRows = (rows: RowDataType[]) => {
    internalExpandedRows.value = rows;
    const finalRows = convertRowsToOriginalFormat(rows);
    emit('update:expanded', finalRows as RowDataType[]);
  };

  const handleChangePagination = (value: PaginationEmitValue) => {
    emit('page-change', transformPaginationEvent(value));
  };

  const handleChangeFilter = (value: GridDataFilter) => {
    emit('on-search', extractTextFilterFromChangeFilter(value));
    emit('changeFilterSearchResult', value.tokenFilter as FilterSelectedValues | undefined);
  };

  const handleChangeSorting = (sortingState: SortingState) => {
    const result = transformSortingEvent(
      sortingState,
      columnsArray.value,
      (col: CustomColumn) => col.field,
    );
    if (result) emit('sortColumn', result);
  };

  const createRowEventPayload = (rowInfo: {
    event: MouseEvent;
    rowData: RowDataType & { rowId?: string; isDetail?: boolean };
    rowIndex: number;
    cellIndex?: string;
    cellName?: string;
  }) => {
    const transformed = transformClickRowEvent<RowDataType>(
      rowInfo,
      rowInfo.cellIndex ?? '',
      rowInfo.cellName ?? '',
    );
    const finalRowData = convertRowToOriginalFormat(transformed.rowData);
    return { ...transformed, rowData: finalRowData };
  };

  const handleClickRow = (rowInfo: {
    event: MouseEvent;
    rowData: RowDataType & { rowId?: string; isDetail?: boolean };
    rowIndex: number;
    cellIndex?: string;
    cellName?: string;
  }) => {
    emit('click-row', createRowEventPayload(rowInfo) as unknown as ClickedRowInfo<RowDataType>);
  };

  const handleDblClickRow = (row: {
    event: MouseEvent;
    rowData: RowDataType & { rowId?: string; isDetail?: boolean };
    rowIndex: number;
    cellIndex?: string;
    cellName?: string;
  }) => {
    emit('dblclick-row', createRowEventPayload(row) as unknown as DblClickedRowInfo<RowDataType>);
  };

  const handleClickCell = (cellInfo: ClickedCellInfoForTransformed<RowDataType>) => {
    const { event, field, value, row, cellIndex } = cellInfo;
    const cleanRow = { ...row } as Record<string, unknown>;
    delete cleanRow.rowId;
    delete cleanRow.isDetail;

    const finalRow = convertRowToOriginalFormat(cleanRow);
    emit('click-cell', {
      event,
      field,
      value,
      row: finalRow,
      cellIndex,
    } as unknown as ClickedCellInfo<RowDataType>);
  };

  const handleClickCellChip = (value: { evt: MouseEvent; chips: string[]; rowIndex: number }) => {
    emit('click-cell-chip', value);
  };

  const handleClickCellChipMore = (value: {
    chips: string[];
    totalChips: string[];
    rowIndex: number;
  }) => {
    emit('click-cell-chip-more', value);
  };

  const handleExportToExcel = (v: { columns: ColumnWithHideInfo[] }) => {
    emit('export-to-excel-from-server', v);
  };

  const handleUpdateSearchWord = (value: string) => {
    emit('update:search-word', value);
  };

  // --- Column Status Handlers ---

  const buildColumnWithHideInfo = (visibilityState?: VisibilityState): ColumnWithHideInfo[] => {
    return columnsArray.value.map(
      (column, index) =>
        ({
          ...column,
          index,
          hiddenDisplay: visibilityState?.[column.field] === false,
        }) as ColumnWithHideInfo,
    );
  };

  const handleChangeColumnVisibility = (visibilityState: VisibilityState) => {
    const columnsWithHideInfo = buildColumnWithHideInfo(visibilityState);
    emit('change-column-info', { type: COLUMN_EVENT_TYPE.DISPLAY, columns: columnsWithHideInfo });
  };

  const handleChangeColumnOrder = (orderState: string[]) => {
    const orderedColumns: ColumnWithHideInfo[] = [];
    const orderedFieldSet = new Set(orderState);

    orderState.forEach((field, index) => {
      const column = columnsArray.value.find((col) => col.field === field);
      if (column) {
        orderedColumns.push({
          ...column,
          index,
          hiddenDisplay: column.hide ?? false,
        } as ColumnWithHideInfo);
      }
    });

    columnsArray.value.forEach((column) => {
      if (!orderedFieldSet.has(column.field)) {
        orderedColumns.push({
          ...column,
          index: orderedColumns.length,
          hiddenDisplay: column.hide ?? false,
        } as ColumnWithHideInfo);
      }
    });

    emit('change-column-info', { type: COLUMN_EVENT_TYPE.ORDER, columns: orderedColumns });
  };

  const handleChangeColumnSize = (value: {
    sizeInfo: Record<string, number>;
    isCustomResized: boolean;
  }) => {
    const columnsWithSize = columnsArray.value.map(
      (column, index) =>
        ({
          ...column,
          index,
          hiddenDisplay: column.hide ?? false,
          width: value.sizeInfo[column.field] ?? column.width,
        }) as ColumnWithHideInfo,
    );

    emit('change-column-info', { type: COLUMN_EVENT_TYPE.RESIZE, columns: columnsWithSize });
  };

  // --- Slot Helpers ---

  const fieldToIndexMap = computed<Record<string, number>>(() => {
    return createFieldToIndexMap(columnsArray.value);
  });

  // props.rows 정규화 (optional prop이라 undefined 가능). 슬롯/인덱스 빌더가 공유.
  const originalRowsRef = computed<unknown[]>(() => (props.rows ?? []) as unknown[]);

  // 트리 모드 슬롯의 getOriginalNode가 O(1) lookup 하도록 originalRows에 대한 인덱스 맵 메모이즈.
  // 비-트리 모드에서는 lookup 자체가 안 일어나므로 undefined를 넘겨 slot.ts의 optional 처리에 맡김.
  const nodeIndices = computed(() =>
    adapterOptions?.isTreeMode ? buildNodeIndices(originalRowsRef.value) : undefined,
  );

  const isRowDisabled = (row: RowDataType): boolean => {
    const disabled = props.disabledRows ?? [];
    if (!disabled.length) return false;

    const disabledObjects = transformRowsIfArray<RowDataType>(disabled);
    const rowObj = row as Record<string, unknown>;

    return disabledObjects.some((disabledRow) => {
      const disabledObj = disabledRow as Record<string, unknown>;
      const keys = Object.keys(disabledObj).filter((k) => k !== 'rowId');

      return keys.every((key) => {
        const rowValue = rowObj[key];
        const disabledValue = disabledObj[key];

        if (Array.isArray(rowValue) && Array.isArray(disabledValue)) {
          return JSON.stringify(rowValue) === JSON.stringify(disabledValue);
        }

        return rowValue === disabledValue;
      });
    });
  };

  const transformSlotItem = (
    modernItem: {
      row: RowDataType & { rowId?: string; isDetail?: boolean };
      rowIndex: number;
      column: unknown;
      cell?: unknown;
    },
    fieldName: string,
  ): BaseGridCompatibleSlotItem<RowDataType> => {
    const isDisabled = isRowDisabled(modernItem.row);

    return createBaseGridSlotItem<RowDataType>({
      modernItem: { ...modernItem, column: modernItem.column as Record<string, unknown> },
      columnFieldName: fieldName,
      columns: columnsArray.value,
      fieldToIndexMap: fieldToIndexMap.value,
      originalRows: originalRowsRef.value,
      isArrayFormat: isRowsArray.value,
      isTreeMode: adapterOptions?.isTreeMode,
      isDisabled,
      rowIdIndex: nodeIndices.value?.rowIdIndex,
      idIndex: nodeIndices.value?.idIndex,
    });
  };

  const transformRowDetailItem = (modernItem: {
    row: RowDataType & { rowId?: string; isDetail?: boolean; detail?: unknown[] };
  }): { row: RowDataType } | { row: [number, string, unknown[]] } => {
    const { row } = modernItem;

    if (!isRowsArray.value) return modernItem;

    const {
      rowId,
      isDetail: _isDetail,
      [DETAIL_ROW_KEY]: _detail,
      ...cleanRow
    } = row as Record<string, unknown>;

    const rowIndex = findRowIndex(modernRows.value, row);
    return {
      row: [
        rowIndex,
        (rowId as string) ?? '',
        transformObjectRowToArray(cleanRow, columnsArray.value),
      ],
    };
  };

  const columnFieldNames = computed<string[]>(() => {
    return columnsArray.value.filter((col) => !col.hide).map((col) => col.field);
  });

  // --- Return ---

  return {
    modernColumns,
    modernOption,
    modernSelectedRow: internalSelectedRow,
    modernCheckedRows: internalCheckedRows,
    modernExpandedRows: internalExpandedRows,
    modernEnvKey,
    rows: modernRows,
    disabledRows: computed(() => {
      const disabled = props.disabledRows ?? [];
      if (!disabled.length || !modernRows.value.length) return [];
      const disabledObjects = transformRowsIfArray<RowDataType>(disabled);
      return findMatchingRowsInModernRows(modernRows.value, disabledObjects);
    }),
    uncheckableRows: computed(() => {
      const uncheckable = props.uncheckable ?? [];
      if (!uncheckable.length) return [];
      return transformRowsIfArray<RowDataType>(uncheckable);
    }),
    tooltipOption: computed(() => props.tooltipOption),
    shownChipCount: computed(() => props.shownChipCount),
    searchWord: computed(() => props.searchWord ?? ''),
    highlightedRows: modernHighlightedRows,
    emptyMessage: computed(() => transformEmptyText(props.option)),
    searchEmptyMessage: computed(() => props.option?.searchEmptyText),
    isRowsArray,
    fieldToIndexMap,
    handleUpdateSelectedRow,
    handleUpdateCheckedRows,
    handleUpdateExpandedRows,
    handleChangePagination,
    handleChangeFilter,
    handleChangeSorting,
    handleClickRow,
    handleDblClickRow,
    handleClickCell,
    handleClickCellChip,
    handleClickCellChipMore,
    handleExportToExcel,
    handleUpdateSearchWord,
    handleChangeColumnVisibility,
    handleChangeColumnOrder,
    handleChangeColumnSize,
    transformSlotItem,
    transformRowDetailItem,
    columnFieldNames,
  };
};
